import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  AlertCircle, 
  Check, 
  ChefHat, 
  Crown, 
  Utensils,
  Calendar,
  CreditCard,
  Loader2,
  ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { apiClient } from '@/services/api/apiClient';
import { stripeApi } from '@/services/api/stripeApi';
import { useNavigate } from 'react-router-dom';

interface ResubscribePromptProps {
  onSuccess?: () => void;
}

interface Plan {
  plan_id: number;
  plan_name: string;
  display_name: string;
  description: string;
  pricing: {
    monthly: number;
    quarterly?: number;
    annual: number;
  };
}

interface SubscriptionStatus {
  status: string;
  can_resubscribe: boolean;
  message: string;
  previous_plan?: string;
  cancelled_at?: string;
  grace_period_end?: string;
  had_subscription?: boolean;
}

const ResubscribePrompt = ({ onSuccess }: ResubscribePromptProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'quarterly' | 'annual'>('monthly');
  const [previousPlan, setPreviousPlan] = useState<string | null>(null);
  const [previousBillingCycle, setPreviousBillingCycle] = useState<string | null>(null);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const [statusResponse, optionsResponse] = await Promise.all([
        apiClient.get('/subscription/status'),
        apiClient.get('/subscription/resubscribe-options')
      ]);

      setStatus(statusResponse.data);
      
      // If user never had a subscription, redirect to plan selection
      if (statusResponse.data.status === 'no_subscription' && !statusResponse.data.had_subscription) {
        navigate('/plan-selection');
        return;
      }
      
      if (statusResponse.data.can_resubscribe) {
        setPlans(optionsResponse.data.plans);
        setPreviousPlan(optionsResponse.data.previous_plan);
        setPreviousBillingCycle(optionsResponse.data.previous_billing_cycle);
        
        // Set defaults based on previous subscription
        if (optionsResponse.data.previous_plan) {
          const prevPlan = optionsResponse.data.plans.find(
            (p: Plan) => p.plan_name === optionsResponse.data.previous_plan
          );
          if (prevPlan) {
            setSelectedPlan(prevPlan.plan_id.toString());
          }
        }
        
        if (optionsResponse.data.previous_billing_cycle) {
          setBillingCycle(optionsResponse.data.previous_billing_cycle);
        }
      }
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
      toast({
        title: "Error",
        description: "Failed to load subscription information",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResubscribe = async () => {
    if (!selectedPlan) {
      toast({
        title: "Please select a plan",
        description: "Choose a subscription plan to continue",
        variant: "destructive"
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiClient.post('/subscription/resubscribe', {
        plan_id: parseInt(selectedPlan),
        billing_cycle: billingCycle
      });

      if (response.data.requires_checkout) {
        // Redirect to Stripe checkout
        const checkoutResponse = await stripeApi.createCheckoutSession(
          parseInt(selectedPlan),
          billingCycle
        );
        window.location.href = checkoutResponse.checkout_url;
      } else {
        // Subscription created successfully
        toast({
          title: "Welcome back!",
          description: "Your subscription has been reactivated successfully.",
        });
        
        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/restaurant-dashboard');
        }
      }
    } catch (error) {
      console.error('Resubscribe error:', error);
      toast({
        title: "Error",
        description: "Failed to reactivate subscription. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getPlanIcon = (planName: string) => {
    switch (planName) {
      case 'yummy': return Utensils;
      case 'delicious': return ChefHat;
      case 'gourmet': return Crown;
      default: return Utensils;
    }
  };

  const getPlanColor = (planName: string) => {
    switch (planName) {
      case 'yummy': return 'from-green-400 to-green-600';
      case 'delicious': return 'from-blue-400 to-blue-600';
      case 'gourmet': return 'from-purple-400 to-purple-600';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  const formatPrice = (price: number, cycle: 'monthly' | 'quarterly' | 'annual') => {
    const formattedPrice = `$${price}`;
    switch (cycle) {
      case 'monthly': return `${formattedPrice}/month`;
      case 'quarterly': return `${formattedPrice}/quarter`;
      case 'annual': return `${formattedPrice}/year`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-white/60" />
      </div>
    );
  }

  if (!status || !status.can_resubscribe) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-4xl mx-auto p-6"
    >
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <div className="p-8">
          {/* Header */}
          <div className="flex items-start gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-yellow-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white mb-2">
                Your Subscription Has Ended
              </h2>
              <p className="text-white/60">
                {status.message || "Reactivate your subscription to continue using all features."}
              </p>
              {status.cancelled_at && (
                <p className="text-white/40 text-sm mt-2">
                  Cancelled on {new Date(status.cancelled_at).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>

          {/* Plan Selection */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">Choose Your Plan</h3>
            <RadioGroup value={selectedPlan} onValueChange={setSelectedPlan}>
              <div className="grid md:grid-cols-3 gap-4">
                {plans.map((plan) => {
                  const Icon = getPlanIcon(plan.plan_name);
                  const gradient = getPlanColor(plan.plan_name);
                  const price = plan.pricing[billingCycle] || plan.pricing.monthly;
                  
                  return (
                    <motion.div
                      key={plan.plan_id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Label
                        htmlFor={`plan-${plan.plan_id}`}
                        className="cursor-pointer"
                      >
                        <Card className={`p-4 border-2 transition-all ${
                          selectedPlan === plan.plan_id.toString()
                            ? 'border-yellow-400 bg-white/15'
                            : 'border-white/20 bg-white/10 hover:border-white/40'
                        }`}>
                          <RadioGroupItem
                            value={plan.plan_id.toString()}
                            id={`plan-${plan.plan_id}`}
                            className="sr-only"
                          />
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`}>
                              <Icon className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-white">
                                {plan.display_name}
                                {previousPlan === plan.plan_name && (
                                  <span className="ml-2 text-xs text-yellow-400">(Previous)</span>
                                )}
                              </h4>
                              <p className="text-white/60 text-sm mt-1">
                                {plan.description}
                              </p>
                              <p className="text-xl font-bold text-white mt-2">
                                {formatPrice(price, billingCycle)}
                              </p>
                            </div>
                          </div>
                        </Card>
                      </Label>
                    </motion.div>
                  );
                })}
              </div>
            </RadioGroup>
          </div>

          {/* Billing Cycle Selection */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">Billing Cycle</h3>
            <RadioGroup 
              value={billingCycle} 
              onValueChange={(value) => setBillingCycle(value as typeof billingCycle)}
            >
              <div className="grid md:grid-cols-3 gap-4">
                <Label htmlFor="monthly" className="cursor-pointer">
                  <Card className={`p-4 border-2 transition-all ${
                    billingCycle === 'monthly'
                      ? 'border-yellow-400 bg-white/15'
                      : 'border-white/20 bg-white/10 hover:border-white/40'
                  }`}>
                    <RadioGroupItem value="monthly" id="monthly" className="sr-only" />
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-white/60" />
                      <div>
                        <p className="font-medium text-white">Monthly</p>
                        <p className="text-white/60 text-sm">Pay month-to-month</p>
                      </div>
                    </div>
                  </Card>
                </Label>

                <Label htmlFor="quarterly" className="cursor-pointer">
                  <Card className={`p-4 border-2 transition-all ${
                    billingCycle === 'quarterly'
                      ? 'border-yellow-400 bg-white/15'
                      : 'border-white/20 bg-white/10 hover:border-white/40'
                  }`}>
                    <RadioGroupItem value="quarterly" id="quarterly" className="sr-only" />
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-white/60" />
                      <div>
                        <p className="font-medium text-white">Quarterly</p>
                        <p className="text-white/60 text-sm">Save 10%</p>
                      </div>
                    </div>
                  </Card>
                </Label>

                <Label htmlFor="annual" className="cursor-pointer">
                  <Card className={`p-4 border-2 transition-all ${
                    billingCycle === 'annual'
                      ? 'border-yellow-400 bg-white/15'
                      : 'border-white/20 bg-white/10 hover:border-white/40'
                  }`}>
                    <RadioGroupItem value="annual" id="annual" className="sr-only" />
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-white/60" />
                      <div>
                        <p className="font-medium text-white">Annual</p>
                        <p className="text-white/60 text-sm">Save 20%</p>
                      </div>
                    </div>
                  </Card>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4">
            <Button
              variant="ghost"
              className="text-white/60 hover:text-white hover:bg-white/10"
              onClick={() => navigate('/products/virtual-assistant')}
            >
              View All Plans
            </Button>
            <Button
              onClick={handleResubscribe}
              disabled={!selectedPlan || submitting}
              className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-semibold"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Reactivate Subscription
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export default ResubscribePrompt;